import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  TextField,
  Checkbox,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  tableCellClasses,
  TableRow,
  TableContainer,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Typography,
  IconButton,
  CircularProgress,
  ListItem,
  List,
  Backdrop,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { LabelWithControl } from "components/common/LabelControl";
import { useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import { getManualOrders } from "redux/actions/manualOrdersActions";
import { processOrders } from "redux/actions/processOrdersActions";
import {
  RESET_PROCESS_ORDERS,
  RESET_AMEND_MAUAL_ORDERS,
  UPDATE_AMEND_MANUAL_ORDERS,
} from "redux/actions/action-type";
import { showNotificationDialogManualOrder } from "redux/actions/notificationManualOrderAction";
import {
  PAGE,
  ORDER_ACTIONS,
  CUTOFF_ACTION,
} from "components/common/CONSTANTS";
import CutOffTimeAlert from "components/common/cutOffTimeAlert";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  "&:first-of-type": {
    width: "4%!important",
    padding: "0px",
  },
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.background.default,
    fontSize: 12,
    border: "1px solid #A9A9A9",
    fontWeight: "bold",
    whiteSpace: "nowrap",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    border: "1px solid #A9A9A9",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: "1px solid #A9A9A9",
  },
}));

export const AmendManualOrders = () => {
  const [orderDateFrom, setOrderDateFrom] = useState(dayjs());
  const [orderDateTo, setOrderDateTo] = useState(dayjs().add(4, "day"));
  const [pin, setPin] = useState("");
  const [min, setMin] = useState("");
  const [depot, setDepot] = useState("");
  const [store, setStore] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [mandatoryFieldsError, setMandatoryFieldsError] = useState([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [newQuantityValues, setNewQuantityValues] = useState({});
  const [newOrderDate, setNewOrderDate] = useState({});
  const [isCreateEnabled, setIsCreateEnabled] = useState(false);
  const [highlightedRows, setHighlightedRows] = useState([]);
  const [isAllRowsSelected, setIsAllRowsSelected] = useState(false);
  const [bulkUpdateQuantity, setBulkUpdateQuantity] = useState();
  const [bulkUpdateQuantityError, setBulkUpdateQuantityError] = useState(false);
  const [offset, setOffset] = useState(0);
  const [reasonCodes, setReasonCodes] = useState();
  const [action, setAction] = useState();
  const [amendedOrders, setAmendedOrders] = useState();
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [isFetchingAllOrders, setIsFetchingAllOrders] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMounted = useRef();
  const tableContainerRef = useRef();
  const isProcessOrder = useRef();

  const limit = 100;

  const userType = useSelector((state) => state.user);
  const { orders, metadata, isManualOrdersLoading, errorDetails } = useSelector(
    (state) => state.manualOrders
  );
  const { isProcessOrderSuccess, processOrderResponse } = useSelector(
    (state) => state.processOrders
  );
  const { reasonCodesResponse } = useSelector((state) => state.reasonCodes);

  const { loginResponse } = userType;
  const { email } = loginResponse;
  const { locationResponse } = useSelector((state) => state.locations);

  const { accessiblePages } = userType.userType;
  const [validStores, setValidStores] = useState([]);

  const openDeleteDialog = () => setIsDeleteDialogOpen(true);
  const closeDeleteDialog = () => setIsDeleteDialogOpen(false);

  const fetchOrders = useCallback(
    (storesToSearch = [], updatedOffset) => {
      const packIdArray = pin
        ? pin.split(",").map((num) => Number(num.trim()))
        : [];
      const itemIdArray = min
        ? min.split(",").map((num) => Number(num.trim()))
        : [];
      const shipFromLocationIdArray = depot
        ? depot.split(",").map((num) => Number(num.trim()))
        : [];
      const shipToLocationIdArray =
        storesToSearch.length > 0 ? storesToSearch : validStores;

      //Validations
      let hasFieldErrors = false;
      if (pin && !isNumericWithCommas(pin)) {
        updateValidationErrors(
          "pin",
          "PIN should contain only numeric values."
        );
        hasFieldErrors = true;
      }
      if (packIdArray.length > 50) {
        updateValidationErrors("pin", "PIN can have a maximum of 50 entries.");
        hasFieldErrors = true;
      }
      if (min && !isNumericWithCommas(min)) {
        updateValidationErrors(
          "min",
          "MIN should contain only numeric values."
        );
        hasFieldErrors = true;
      }
      if (itemIdArray.length > 50) {
        updateValidationErrors("min", "MIN can have a maximum of 50 entries.");
        hasFieldErrors = true;
      }
      if (depot && !isNumericWithCommas(depot)) {
        updateValidationErrors(
          "depot",
          "Depot should contain only numeric values."
        );
        hasFieldErrors = true;
      }
      if (shipFromLocationIdArray.length > 50) {
        updateValidationErrors(
          "depot",
          "Depot can have a maximum of 50 entries."
        );
        hasFieldErrors = true;
      }
      if (store && !isNumericWithCommas(store)) {
        updateValidationErrors(
          "store",
          "Store should contain only numeric values."
        );
        hasFieldErrors = true;
      }
      if (orderDateFrom && !dayjs(orderDateFrom).isValid()) {
        updateValidationErrors(
          "orderDateFrom",
          "Please enter date in correct format"
        );
        hasFieldErrors = true;
      }
      if (orderDateTo && !dayjs(orderDateTo).isValid()) {
        updateValidationErrors(
          "orderDateTo",
          "Please enter date in correct format"
        );
        hasFieldErrors = true;
      }
      if (orderDateFrom && orderDateFrom.isBefore(dayjs(), "day")) {
        updateValidationErrors(
          "orderDateFrom",
          "Please enter current or future date"
        );
        hasFieldErrors = true;
      }
      if (orderDateTo && orderDateTo.isBefore(dayjs(), "day")) {
        updateValidationErrors(
          "orderDateTo",
          "Please enter current or future date"
        );
        hasFieldErrors = true;
      }

      if (!hasFieldErrors) {
        const requestBody = {
          orderDateFrom: dayjs(orderDateFrom).format("YYYY-MM-DD"),
          orderDateTo: dayjs(orderDateTo).format("YYYY-MM-DD"),
          packId: packIdArray,
          itemId: itemIdArray,
          shipFromLocationId: shipFromLocationIdArray,
          shipToLocationId: shipToLocationIdArray,
          isSohData: true,
          userType: "CENTRAL_SUPPLYCHAIN",
        };
        dispatch(
          getManualOrders({
            requestBody: requestBody,
            limit: limit,
            offset: updatedOffset !== undefined ? updatedOffset : offset,
          })
        );
      }
    },
    [
      depot,
      dispatch,
      min,
      offset,
      orderDateFrom,
      orderDateTo,
      pin,
      validStores,
      store,
    ]
  );

  const handleInfiniteScroll = useCallback(async () => {
    try {
      const { scrollTop, scrollHeight, clientHeight } =
        tableContainerRef.current;
      if (
        scrollTop + clientHeight >= scrollHeight - 1 &&
        !isManualOrdersLoading
      ) {
        setOffset(orders.length);
      }
    } catch (error) {
      console.log(error, "error");
    }
  }, [isManualOrdersLoading, tableContainerRef, orders]);

  useEffect(() => {
    if (isMounted.current) return;

    isMounted.current = true;
    isProcessOrder.current = false;
    if (
      processOrderResponse &&
      Object.hasOwn(processOrderResponse, "metadata")
    ) {
      isProcessOrder.current = true;
    }
    dispatch({ type: RESET_PROCESS_ORDERS });
    if (accessiblePages) {
      let channelList = accessiblePages[PAGE.AMEND_MANUAL_ORDERS.key];
      const stores = [];
      locationResponse.forEach(function (location) {
        if (channelList.indexOf(location.channel.toLowerCase()) > -1) {
          stores.push(location.locationId);
        }
      });
      setValidStores(stores);
      if (stores.length > 0) {
        fetchOrders(stores);
      }
    }

    return () => {
      dispatch({ type: RESET_AMEND_MAUAL_ORDERS });
    };
  }, [accessiblePages, locationResponse]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    tableContainer.addEventListener("scroll", handleInfiniteScroll);
    return () =>
      tableContainer.removeEventListener("scroll", handleInfiniteScroll);
  }, [isManualOrdersLoading, handleInfiniteScroll]);

  //Disabled eslint check. Adding missing dependency causes rerenders.
  useEffect(() => {
    if (metadata && metadata.totalCount > offset && !isFetchingAllOrders) {
      fetchOrders();
    }
  }, [offset]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (reasonCodesResponse) {
      const { reasonCodes: allReasonCodes } = reasonCodesResponse;
      const filteredReasonCodes = allReasonCodes.filter(
        (reason) => reason.orderType === "Manual Orders"
      );
      const uniqueReasonCodes = Array.from(
        filteredReasonCodes
          .reduce((map, item) => {
            if (!map.has(item.reasonCode)) {
              map.set(item.reasonCode, item);
            }
            return map;
          }, new Map())
          .values()
      );
      setReasonCodes(uniqueReasonCodes);
    }
  }, [reasonCodesResponse, setReasonCodes]);

  useEffect(() => {
    if (isProcessOrderSuccess) {
      setHighlightedRows([]);
      closeDeleteDialog();
      setSelectedRows([]);
      setIsAllRowsSelected(false);
      setIsCreateEnabled(false);
      setNewQuantityValues({});
      setNewOrderDate({});

      const { metadata } = processOrderResponse;
      const {
        validationfailed,
        totalrecordsProcessed,
        ordersDeleted,
        totalrecordsRecieved,
      } = metadata;

      if (validationfailed > 0 && isProcessOrder.current === false) {
        navigate(PAGE.ERROR_FEEDBACK.url, {
          state: {
            action: ORDER_ACTIONS.AMEND,
          },
        });
      }
      if (validationfailed === 0) {
        let title;
        let textList;
        let textItems;
        let message;
        if (action === ORDER_ACTIONS.DELETE && isBulkDelete) {
          dispatch({ type: RESET_AMEND_MAUAL_ORDERS });
          setIsBulkDelete(false);
          const updatedOffset = 0;
          setOffset(updatedOffset);
          fetchOrders(updatedOffset);

          title = "Manual orders deleted successfully.";
          textList = [`${ordersDeleted} order(s) deleted. `];

          textItems = textList?.map((x, index) => (
            <ListItem
              dense
              sx={{ p: 0, display: "list-item" }}
              key={index}
            >{`${x}`}</ListItem>
          ));

          message = (
            <List dense sx={{ listStyleType: "disc" }}>
              {textItems}
            </List>
          );
        }
        if (action === ORDER_ACTIONS.DELETE && !isBulkDelete) {
          title = "Manual orders deleted successfully.";
          textList = [`${ordersDeleted} order(s) deleted. `];
          const updatedData = orders.filter(
            (order) =>
              !amendedOrders.some(
                (amendedOrder) =>
                  amendedOrder.manualOrderId === order.manualOrderId
              )
          );
          dispatch({
            type: UPDATE_AMEND_MANUAL_ORDERS,
            payload: { orders: updatedData },
          });

          textItems = textList?.map((x, index) => (
            <ListItem
              dense
              sx={{ p: 0, display: "list-item" }}
              key={index}
            >{`${x}`}</ListItem>
          ));

          message = (
            <List dense sx={{ listStyleType: "disc" }}>
              {textItems}
            </List>
          );
        }
        if (action === ORDER_ACTIONS.AMEND) {
          title = "Manual Orders Submitted Successfully";
          textList = [
            `Total number of manual orders : ${totalrecordsRecieved} `,
            `Manual orders created = ${totalrecordsProcessed}`,
          ];
          const updatedData = orders.map((order) => {
            const amendedOrder = amendedOrders.find(
              (amended) => amended.manualOrderId === order.manualOrderId
            );

            if (amendedOrder) {
              return {
                ...order,
                quantityOrdered: amendedOrder.quantityOrdered,
                orderDate: amendedOrder.orderDate,
              };
            }

            return order;
          });
          dispatch({
            type: UPDATE_AMEND_MANUAL_ORDERS,
            payload: { orders: updatedData },
          });

          textItems = textList?.map((x, index) => (
            <ListItem
              dense
              sx={{ p: 0, display: "list-item" }}
              key={index}
            >{`${x}`}</ListItem>
          ));

          message = (
            <>
              <Typography variant="span">
                Manual Orders submitted successfully, refer below for the exact
                status:{" "}
              </Typography>
              <List dense sx={{ listStyleType: "disc" }}>
                {textItems}
              </List>
            </>
          );
        }

        dispatch(showNotificationDialogManualOrder(title, message, "Done", ""));
        dispatch({ type: RESET_PROCESS_ORDERS });
        setAmendedOrders();
        setAction();
      }
    }
  }, [
    isProcessOrderSuccess,
    processOrderResponse,
    dispatch,
    email,
    min,
    pin,
    store,
    depot,
    navigate,
    fetchOrders,
    action,
    amendedOrders,
    orders,
    isBulkDelete,
    isProcessOrder,
  ]);

  useEffect(() => {
    if (highlightedRows.length > 0) {
      setIsCreateEnabled(true);
    } else {
      setIsCreateEnabled(false);
    }
  }, [highlightedRows]);

  useEffect(() => {
    if (orders.length > 0) {
      if (isAllRowsSelected) {
        const allOrders = orders.map((item) => item.manualOrderId);
        setSelectedRows(allOrders);

        if (bulkUpdateQuantity) {
          appylQuantityBulkUpdate();
        }
      }
    }
  }, [orders.length]); //eslint-disable-line react-hooks/exhaustive-deps

  const isNumericWithCommas = (value) => {
    return value.split(",").every((v) => /^[0-9, \s]*$/.test(v.trim()));
  };

  const updateValidationErrors = (field, message) => {
    setValidationErrors((previous) => ({
      ...previous,
      [field]: {
        error: true,
        message: message,
      },
    }));
  };

  const handleSearch = () => {
    dispatch({ type: RESET_AMEND_MAUAL_ORDERS });
    const errors = [];
    const validSearchStores = [];
    setValidationErrors({});

    if (errors.length > 0) {
      setMandatoryFieldsError(errors);
      return;
    } else {
      if (store) {
        const storeArr = store.split(",").map((x) => parseInt(x));
        const invalidStores = [];
        storeArr.forEach(function (store) {
          if (validStores.indexOf(store) < 0) {
            invalidStores.push(store);
          } else {
            validSearchStores.push(store);
          }
        });
        if (invalidStores.length > 0) {
          errors.push(
            `The following store(s) are not valid: ${invalidStores.join(", ")}`
          );
        }
      }
      setMandatoryFieldsError([]);
    }

    if (!store || validSearchStores.length > 0) {
      dispatch({ type: RESET_AMEND_MAUAL_ORDERS });
      const updatedOffset = 0;
      setOffset(updatedOffset);
      fetchOrders(validSearchStores, updatedOffset);
      setSelectedRows([]);
      setIsAllRowsSelected(false);
      setNewQuantityValues({});
      setNewOrderDate({});
    }
  };

  const handleCheckboxChange = (manualOrderId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(manualOrderId)
        ? prevSelectedRows.filter((row) => row !== manualOrderId)
        : [...prevSelectedRows, manualOrderId]
    );
  };

  const handleSelectAllRows = async () => {
    if (isAllRowsSelected) {
      setSelectedRows([]);
      setIsAllRowsSelected(false);
      setNewQuantityValues({});
      setNewOrderDate({});
    } else {
      setIsFetchingAllOrders(true);
      const allOrders = orders.map((item) => item.manualOrderId);
      setSelectedRows(allOrders);
      setIsAllRowsSelected(true);

      if (metadata && metadata.totalCount > orders.length) {
        let offset = orders.length;

        // Keep fetching until we have all orders
        while (offset < metadata.totalCount) {
          const requestBody = {
            orderDateFrom: dayjs(orderDateFrom).format("YYYY-MM-DD"),
            orderDateTo: dayjs(orderDateTo).format("YYYY-MM-DD"),
            packId: pin ? pin.split(",").map((num) => Number(num.trim())) : [],
            itemId: min ? min.split(",").map((num) => Number(num.trim())) : [],
            shipFromLocationId: depot
              ? depot.split(",").map((num) => Number(num.trim()))
              : [],
            shipToLocationId: store
              ? store.split(",").map((num) => Number(num.trim()))
              : validStores,
            isSohData: true,
            userType: "CENTRAL_SUPPLYCHAIN",
          };

          // Create a new scope for the currentOffset
          const currentOffset = offset;

          await new Promise((resolve) => {
            dispatch(
              getManualOrders({
                requestBody,
                limit,
                offset: currentOffset,
              })
            ).then(() => {
              resolve();
            });
          });

          offset += limit;
        }
      }
      setIsFetchingAllOrders(false);
    }
  };

  const handleDelete = () => {
    const updatedData = orders.map((item) => {
      return { ...item, quantityOrdered: 0 };
    });
    const formattedData = formatDates(updatedData);
    setAction(ORDER_ACTIONS.DELETE);
    setIsBulkDelete(true);
    dispatch(
      processOrders({ orders: formattedData, action: ORDER_ACTIONS.DELETE })
    );
    isProcessOrder.current = false;
  };

  const handleDateChange = (manualOrderId, value) => {
    const newDate = dayjs(value).format("YYYY-MM-DD");
    const existingDate = orders.find(
      (item) => item.manualOrderId === manualOrderId
    ).orderDate;

    if (newDate && newDate !== existingDate) {
      setHighlightedRows((prev) => [...prev, manualOrderId]);
    } else {
      setHighlightedRows((prev) =>
        prev.filter((highlightOrder) => highlightOrder !== manualOrderId)
      );
    }

    setNewOrderDate((prev) => ({
      ...prev,
      [manualOrderId]: dayjs(value).format("YYYY-MM-DD"),
    }));
  };

  const handleQuantityChange = (manualOrderId, value) => {
    const newQuantity = value === "" || isNaN(value) ? "" : parseInt(value, 10);
    const underMin = newQuantity === 0;
    const existingQuantity = orders.find(
      (item) => item.manualOrderId === manualOrderId
    ).quantityOrdered;

    if (newQuantity && newQuantity !== existingQuantity && !underMin) {
      setHighlightedRows((prev) => [...prev, manualOrderId]);
    } else {
      setHighlightedRows((prev) =>
        prev.filter((highlightOrder) => highlightOrder !== manualOrderId)
      );
    }

    setNewQuantityValues((prev) => ({
      ...prev,
      [manualOrderId]: newQuantity,
    }));
  };

  const formatDates = (orders) => {
    return orders.map((order) => {
      const formattedOrder = { ...order };
      if (formattedOrder.orderDate) {
        formattedOrder.orderDate = dayjs(formattedOrder.orderDate).format(
          "YYYY-MM-DDTHH:mm:ss"
        );
      }

      return formattedOrder;
    });
  };

  const handleUpdate = () => {
    const dateOrderIds = Object.keys(newOrderDate);
    const quantityOrderids = Object.keys(newQuantityValues);

    const updatedData = [];
    orders.forEach((item) => {
      if (
        dateOrderIds.includes(String(item.manualOrderId)) &&
        quantityOrderids.includes(String(item.manualOrderId))
      ) {
        updatedData.push({
          ...item,
          quantityOrdered: newQuantityValues[item.manualOrderId],
          orderDate: newOrderDate[item.manualOrderId],
        });
      } else if (dateOrderIds.includes(String(item.manualOrderId))) {
        updatedData.push({
          ...item,
          orderDate: newOrderDate[item.manualOrderId],
        });
      } else if (quantityOrderids.includes(String(item.manualOrderId))) {
        updatedData.push({
          ...item,
          quantityOrdered: newQuantityValues[item.manualOrderId],
        });
      }
    });

    const formattedData = formatDates(updatedData);
    setAction(ORDER_ACTIONS.AMEND);
    setAmendedOrders(updatedData);
    dispatch(
      processOrders({ orders: formattedData, action: ORDER_ACTIONS.AMEND })
    );
    isProcessOrder.current = false;
  };

  const disableForOnlyPastDate = (date) => {
    const today = dayjs();
    return date.isBefore(today, "day");
  };

  const disableOrderDateTo = (date) => {
    const fromDate = dayjs(orderDateFrom);
    const toDate = fromDate.add(4, "day");
    const currentDate = dayjs(date);

    return (
      currentDate.isBefore(fromDate, "day") ||
      currentDate.isAfter(toDate, "day")
    );
  };

  const handleRowDelete = (row) => {
    const updatedData = [{ ...row, quantityOrdered: 0 }];
    const orders = formatDates(updatedData);
    setAction(ORDER_ACTIONS.DELETE);
    setAmendedOrders(orders);
    dispatch(processOrders({ orders: orders, action: ORDER_ACTIONS.DELETE }));
    isProcessOrder.current = false;
  };

  const handleBulkUpdateQuantityChange = (event) => {
    const newQuantity =
      event.target.value === "" || isNaN(event.target.value)
        ? ""
        : parseInt(event.target.value, 10);
    const underMin = newQuantity === 0;
    setBulkUpdateQuantity(newQuantity);
    if (!underMin) {
      setBulkUpdateQuantityError(false);
    } else {
      setBulkUpdateQuantityError(true);
    }
  };

  const appylQuantityBulkUpdate = () => {
    let updatedData;
    const highlightedOrderIds = [];
    orders.forEach((item) => {
      updatedData = {
        ...updatedData,
        [item.manualOrderId]: bulkUpdateQuantity,
      };
      highlightedOrderIds.push(item.manualOrderId);
    });

    setHighlightedRows(highlightedOrderIds);
    setNewQuantityValues(updatedData);
  };

  const ReasonCell = ({ reasonCode }) => {
    let description = "";
    reasonCodes?.forEach((reason) => {
      if (reason.reasonCode === reasonCode) {
        description = reason.description;
      }
    });
    return <>{description}</>;
  };

  return (
    <div className="App mainPageStyling">
      <div style={{ display: "flex", alignItems: "center" }}>
        {isFetchingAllOrders && (
          <Backdrop
            sx={{
              color: "#fff",
              flexDirection: "column",
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
            open={true}
          >
            <CircularProgress color="inherit" />

            <Typography variant="h6" sx={{ marginTop: 2 }}>
              Loading...
            </Typography>
          </Backdrop>
        )}
        <Typography component="h1" variant="h5" sx={{ mb: 2, mr: 2 }}>
          Amend Manual Orders
        </Typography>
        <CutOffTimeAlert actionType={CUTOFF_ACTION.amend_delete_manual_order} />
      </div>
      <div style={{ marginBottom: "10px" }} className="labelControl">
        <Grid sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          <Grid container spacing={3} sx={{ "& > *": { flex: 1 } }}>
            <Grid item xs={12} sm={6} md={1.5}>
              <LabelWithControl label="Order Date From" labelId="orderDateFrom">
                <DatePicker
                  format="DD/MM/YYYY"
                  id="orderDateFrom"
                  variant="outlined"
                  size="small"
                  value={orderDateFrom}
                  shouldDisableDate={disableForOnlyPastDate}
                  onChange={(newValue) => {
                    setOrderDateFrom(newValue);
                    setOrderDateTo(dayjs(newValue).add(4, "day"));
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      placeholder: "Order Date From ",
                      helperText: validationErrors?.orderDateFrom?.message,
                      error: validationErrors?.orderDateFrom?.error,
                      inputProps: { 'data-testid': 'order-date-from-input' },
                    },
                  }}
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <LabelWithControl label="Order Date To" labelId="orderDateto">
                <DatePicker
                  format="DD/MM/YYYY"
                  id="orderDateTo"
                  variant="outlined"
                  size="small"
                  value={orderDateTo}
                  minValue={orderDateFrom}
                  shouldDisableDate={disableOrderDateTo}
                  onChange={(newValue) => setOrderDateTo(newValue)}
                  slotProps={{
                    textField: {
                      size: "small",
                      placeholder: "Order Date To",
                      helperText: validationErrors?.orderDateTo?.message,
                      error: validationErrors?.orderDateTo?.error,
                      inputProps: { 'data-testid': 'order-date-to-input' },
                    },
                  }}
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabelWithControl
                label={<>MIN &nbsp;or&nbsp; PIN</>}
              ></LabelWithControl>
              <TextField
                id="min"
                variant="outlined"
                size="small"
                placeholder="Min"
                value={min}
                style={{ width: "49%", float: "left" }}
                onChange={(e) => {
                  setMin(e.target.value);
                  if (e.target.value) {
                    setPin("");
                  }
                }}
                disabled={pin.length > 0}
                inputProps={{
                  style: {
                    background: pin.length > 0 ? "#f5f5f5" : "inherit",
                  },
                }}
                helperText={validationErrors?.min?.message}
                error={validationErrors?.min?.error}
              />
              <TextField
                id="pin"
                variant="outlined"
                size="small"
                placeholder="Pin"
                value={pin}
                style={{ width: "49%", float: "left", paddingLeft: "24px" }}
                onChange={(e) => {
                  setPin(e.target.value);
                  if (e.target.value) {
                    setMin("");
                  }
                }}
                disabled={min.length > 0}
                inputProps={{
                  style: {
                    background: min.length > 0 ? "#f5f5f5" : "inherit",
                  },
                }}
                helperText={validationErrors?.pin?.message}
                error={validationErrors?.pin?.error}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <LabelWithControl label="Depot ID" labelId="depotId">
                <TextField
                  id="depotId"
                  variant="outlined"
                  size="small"
                  placeholder="Depot"
                  value={depot}
                  onChange={(e) => setDepot(e.target.value)}
                  helperText={validationErrors?.depot?.message}
                  error={validationErrors?.depot?.error}
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <LabelWithControl label="Store ID" labelId="storeId">
                <TextField
                  id="storeId"
                  variant="outlined"
                  size="small"
                  placeholder="Store"
                  value={store}
                  onChange={(e) => setStore(e.target.value)}
                  helperText={validationErrors?.store?.message}
                  error={validationErrors?.store?.error}
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  width: "auto",
                  height: "25px",
                  fontSize: "12px",
                  padding: "3px",
                  marginTop: "16px",
                }}
                onClick={() => {
                  handleSearch();
                }}
                fullWidth
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <Grid item>
          {mandatoryFieldsError.length > 0 && (
            <div className="error-display">
              {mandatoryFieldsError.map((error, idx) => (
                <p key={idx} style={{ color: "red" }}>
                  {error}
                </p>
              ))}
            </div>
          )}
        </Grid>
      </div>

      {isAllRowsSelected && (
        <div
          style={{
            padding: "5px",
            textAlign: "left",
            marginBottom: "5px",
            backgroundColor: "rgba(241,243,244,0.87)",
          }}
        >
          <div
            className="bulkQty labelControl"
            style={{ display: "flex", alignItems: "center" }}
          >
            <span>Enter Quantity for Bulk Update:</span>{" "}
            <TextField
              variant="outlined"
              size="small"
              type="text"
              value={bulkUpdateQuantity}
              sx={{ width: 60 }}
              onChange={handleBulkUpdateQuantityChange}
              inputProps={{
                maxLength: 4,
              }}
            />
            {bulkUpdateQuantityError && (
              <Tooltip title={`Quantity should be greater than 0`} arrow>
                <InfoOutlinedIcon style={{ color: "#F28C28" }} />
              </Tooltip>
            )}
            <Button
              variant="contained"
              color="primary"
              sx={{ marginLeft: 4, fontSize: "12px", height: "25px" }}
              onClick={appylQuantityBulkUpdate}
              disableRipple
              disabled={bulkUpdateQuantityError}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
      <TableContainer
        component={Paper}
        className="tableContainerScroll AmendWrapper tableStructure tableStyle tableStyleAmend"
        ref={tableContainerRef}
      >
        <Table sx={{ minWidth: 700 }}>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>
                <Tooltip title="Select all rows for bulk actions" arrow>
                  <Checkbox
                    onChange={handleSelectAllRows}
                    className="checkboxStyle"
                    disabled={
                      !isManualOrdersLoading && orders && orders.length === 0
                    }
                  />
                </Tooltip>
              </StyledTableCell>
              <StyledTableCell>MIN</StyledTableCell>
              <StyledTableCell>PIN</StyledTableCell>
              <StyledTableCell style={{ flex: 1, minWidth: 400 }}>
                Description
              </StyledTableCell>
              <StyledTableCell>Pack Size</StyledTableCell>
              <StyledTableCell>Depot</StyledTableCell>
              <StyledTableCell>Current SOH</StyledTableCell>
              <StyledTableCell>Store</StyledTableCell>
              <StyledTableCell>Order Date</StyledTableCell>
              <StyledTableCell style={{ flex: 1, minWidth: 100 }}>
                Quantity
              </StyledTableCell>
              <StyledTableCell>Reason</StyledTableCell>
              <StyledTableCell>Action</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {orders &&
              orders.length > 0 &&
              orders
                .filter((item) => item.approvedBy !== "")
                .map(
                  (item) =>
                    !item.deleted && (
                      <StyledTableRow
                        key={item.manualOrderId}
                        sx={{
                          backgroundColor: highlightedRows.includes(
                            item.manualOrderId
                          )
                            ? "rgba(200, 230, 201, 1)!important"
                            : "inherit",
                        }}
                      >
                        <StyledTableCell>
                          <Checkbox
                            checked={selectedRows.includes(item.manualOrderId)}
                            onChange={() =>
                              handleCheckboxChange(item.manualOrderId)
                            }
                            disabled
                            className="checkboxStyle"
                          />
                        </StyledTableCell>
                        <StyledTableCell>{item.itemId}</StyledTableCell>
                        <StyledTableCell>{item.packId}</StyledTableCell>
                        <StyledTableCell style={{ flex: 1, minWidth: 100 }}>
                          <Tooltip title={item.itemDescription} arrow>
                            <span>{item.itemDescription}</span>
                          </Tooltip>
                        </StyledTableCell>
                        <StyledTableCell style={{ flex: 1, minWidth: 30 }}>
                          {item.packSize}
                        </StyledTableCell>
                        <StyledTableCell>
                          {item.shipFromLocationId}
                        </StyledTableCell>
                        <StyledTableCell>{item.sohQuantity}</StyledTableCell>
                        <StyledTableCell>
                          {item.shipToLocationId}
                        </StyledTableCell>
                        <StyledTableCell style={{ flex: 1, minWidth: 120 }}>
                          <div>
                            <DatePicker
                              format="DD/MM/YYYY"
                              className="inputBox"
                              shouldDisableDate={disableForOnlyPastDate}
                              value={
                                newOrderDate[item.manualOrderId] !== undefined
                                  ? dayjs(newOrderDate[item.manualOrderId])
                                  : dayjs(item.orderDate)
                              }
                              onChange={(newValue) =>
                                handleDateChange(
                                  item.manualOrderId,
                                  dayjs(newValue).format("YYYY-MM-DD")
                                )
                              }
                              slotProps={{
                                textField: {        
                                  inputProps: { 'data-testid': 'order-date-input' },
                                },
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  disabled={item.approved}
                                />
                              )}
                            />
                          </div>
                        </StyledTableCell>
                        <StyledTableCell className="quantityIcon">
                          <TextField
                            className="inputBox"
                            type="text"
                            value={
                              newQuantityValues[item.manualOrderId] !==
                              undefined
                                ? newQuantityValues[item.manualOrderId]
                                : item.quantityOrdered
                            }
                            onChange={(e) =>
                              handleQuantityChange(
                                item.manualOrderId,
                                e.target.value
                              )
                            }
                            InputProps={{
                              style: {
                                width: 60,
                              },
                            }}
                            inputProps={{
                              maxLength: 4,
                            }}
                          />
                          {newQuantityValues[item.manualOrderId] === 0 ? (
                            <Tooltip title="Quantity should be greater than 0 ">
                              <InfoOutlinedIcon
                                sx={{
                                  color: "#F28C28",
                                  marginTop: "8px",
                                  fontSize: "18px",
                                }}
                              />
                            </Tooltip>
                          ) : (
                            ""
                          )}
                        </StyledTableCell>
                        <StyledTableCell>
                          <ReasonCell reasonCode={item.reasonCode} />
                        </StyledTableCell>
                        <StyledTableCell style={{ flex: 1, minWidth: 40 }}>
                          <IconButton
                            aria-label="delete"
                            onClick={() => handleRowDelete(item)}
                            color="gray"
                            className="deleteIcon"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </StyledTableCell>
                      </StyledTableRow>
                    )
                )}

            {isManualOrdersLoading && (
              <TableRow>
                <TableCell
                  colSpan={14}
                  style={{
                    textAlign: "center",
                    position: "relative",
                    height: "100px",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 12,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <CircularProgress />
                  </div>
                </TableCell>
              </TableRow>
            )}

            {!isManualOrdersLoading &&
              (!orders || (orders && orders.length === 0)) && (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    {errorDetails.errorMessage && (
                      <>{errorDetails.errorMessage}</>
                    )}
                    {!errorDetails.errorMessage && <>No data found</>}
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth={true}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the selected orders?
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="primary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Grid
        container
        spacing={2}
        sx={{ marginTop: "10px", marginBottom: "10px" }}
        className="paddingTop0 commonButtons marginBottom0"
      >
        <Grid item xs={12} sm={6} md={10} className="innerPadding">
          <Button
            variant="contained"
            onClick={openDeleteDialog}
            disabled={!isAllRowsSelected}
          >
            Delete
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={1} className="innerPadding">
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdate}
            disabled={!isCreateEnabled}
          >
            Update
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={1} className="innerPadding">
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(PAGE.HOME.url)}
          >
            Cancel
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};
